import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { Spin, message } from 'antd'
import { CheckOutlined } from '@ant-design/icons'

const PageContainer = styled.div`
  min-height: 100vh;
  background: #fff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto',
    sans-serif;
`

const Header = styled.div`
  background: #f8f9fa;
  padding: 20px;
  border-bottom: 1px solid #e9ecef;
  text-align: center;
`

const SearchContainer = styled.div`
  margin: 20px auto;
  max-width: 400px;
`

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  outline: none;
  transition: border-color 0.3s ease;

  &:focus {
    border-color: #b4d820;
  }

  &::placeholder {
    color: #999;
  }
`

const VenueLogo = styled.img`
  max-height: 80px;
  max-width: 200px;
  margin-bottom: 10px;
`

const VenueName = styled.h1`
  margin: 0;
  font-size: 28px;
  color: #333;
  font-weight: 600;
`

const MenuName = styled.h2`
  margin: 5px 0 0 0;
  font-size: 20px;
  color: #666;
  font-weight: 400;
`

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`

const AllergenTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`

const TableHeader = styled.th`
  background: #f5f5f5;
  padding: 12px 8px;
  text-align: center;
  font-weight: bold;
  border-bottom: 2px solid #ddd;
  border-right: 1px solid #ddd;
  font-size: 12px;

  &:first-child {
    border-right: 2px solid #ddd;
    text-align: left;
    width: 200px;
  }

  &:last-child {
    border-right: none;
  }
`

const TableCell = styled.td`
  padding: 10px 8px;
  text-align: center;
  border-bottom: 1px solid #ddd;
  border-right: 1px solid #ddd;
  font-size: 11px;

  &:first-child {
    border-right: 2px solid #ddd;
    text-align: left;
    font-weight: 500;
  }

  &:last-child {
    border-right: none;
  }
`

const SectionHeader = styled.tr`
  background: #f6ffed !important;

  td {
    background: #f6ffed !important;
    border-bottom: 1px solid #b4d820;
    font-weight: bold;
    color: #b4d820;
    font-size: 14px;
    padding: 12px 8px;
  }
`

const Footer = styled.div`
  text-align: center;
  margin-top: 40px;
  padding: 20px;
  border-top: 1px solid #e9ecef;
  color: #666;
  font-size: 12px;
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`

const RotateDeviceOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.95);
  display: none;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  flex-direction: column;
  padding: 20px;
  text-align: center;

  @media screen and (max-width: 768px) and (orientation: portrait) {
    display: flex;
  }
`

const RotateIcon = styled.div`
  font-size: 80px;
  margin-bottom: 30px;
  animation: rotate 2s ease-in-out infinite;

  @keyframes rotate {
    0%,
    100% {
      transform: rotate(0deg);
    }
    50% {
      transform: rotate(90deg);
    }
  }
`

const RotateMessage = styled.div`
  color: white;
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 10px;
`

const RotateSubMessage = styled.div`
  color: #ccc;
  font-size: 16px;
  max-width: 300px;
`

const CustomerAllergenPage = () => {
  const [menuData, setMenuData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')

  const defaultAllergens = [
    'Celery',
    'Gluten',
    'Crustaceans',
    'Eggs',
    'Fish',
    'Lupin',
    'Milk',
    'Molluscs',
    'Mustard',
    'Nuts',
    'Peanuts',
    'Sesame seeds',
    'Soya',
    'Sulphites',
  ]

  const fetchMenuData = async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search)
      const menuId = urlParams.get('menu')

      if (!menuId) {
        setError('No menu specified')
        setLoading(false)
        return
      }

      // Add cache-busting parameter to ensure fresh data
      const cacheBuster = `?t=${Date.now()}`
      const response = await fetch(
        `${process.env.REACT_APP_EXPRESS_API_ENDPOINT}menus/${menuId}/allergen-grid${cacheBuster}`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )

      if (!response.ok) {
        throw new Error('Failed to fetch menu data')
      }

      const data = await response.json()

      // Debug logging to check what data we're receiving
      // console.log('CustomerAllergenPage: Fetched menu data:', data)
      if (data.dishes && data.dishes.length > 0) {
        const quickCocktail = data.dishes.find(
          dish =>
            dish.name && dish.name.toLowerCase().includes('quick cocktail'),
        )
        if (quickCocktail) {
          console.log('CustomerAllergenPage: Quick Cocktail data:', {
            name: quickCocktail.name,
            allergens: quickCocktail.allergens,
            allergenTraces: quickCocktail.allergenTraces,
            allergensArray: quickCocktail.allergens,
            allergenTracesArray: quickCocktail.allergenTraces,
          })
          // console.log('CustomerAllergenPage: Quick Cocktail allergens:', quickCocktail.allergens)
          // console.log('CustomerAllergenPage: Quick Cocktail allergenTraces:', quickCocktail.allergenTraces)
        }
      }

      if (data.success) {
        setMenuData(data)
      } else {
        throw new Error(data.message || 'Failed to load menu data')
      }
    } catch (err) {
      console.error('Error fetching menu data:', err)
      setError(err.message)
      message.error('Failed to load allergen information')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMenuData()

    // Auto-refresh every 30 seconds to ensure data is always fresh
    const interval = setInterval(() => {
      // console.log('Auto-refreshing customer allergen page...')
      fetchMenuData()
    }, 30000) // 30 seconds

    return () => clearInterval(interval)
  }, [])

  const renderAllergenTable = () => {
    if (!menuData || !menuData.dishes) return null

    // Filter dishes based on search term
    let filteredDishes = menuData.dishes
    if (searchTerm.trim()) {
      filteredDishes = menuData.dishes.filter(dish =>
        dish.name.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    }

    // Group dishes by section - only include dishes with actual sections
    const dishesBySection = {}
    filteredDishes.forEach(dish => {
      if (dish.section) {
        // Only include dishes with actual sections
        const sectionName = dish.section
        if (!dishesBySection[sectionName]) {
          dishesBySection[sectionName] = []
        }
        dishesBySection[sectionName].push(dish)
      }
    })

    const tableRows = []

    Object.keys(dishesBySection).forEach(sectionName => {
      if (dishesBySection[sectionName].length > 0) {
        // Add section header
        tableRows.push(
          <SectionHeader key={`section-${sectionName}`}>
            <TableCell colSpan={defaultAllergens.length + 1}>
              {sectionName}
            </TableCell>
          </SectionHeader>,
        )

        // Add dishes for this section
        dishesBySection[sectionName].forEach((dish, index) => {
          const dishAllergens = Array.isArray(dish.allergens)
            ? dish.allergens
            : []
          const dishAllergenTraces = Array.isArray(dish.allergenTraces)
            ? dish.allergenTraces
            : []

          tableRows.push(
            <tr key={`${sectionName}-${dish.name}-${index}`}>
              <TableCell>{dish.name}</TableCell>
              {defaultAllergens.map(allergen => {
                // Check for "Yes" allergens first
                const hasAllergen = dishAllergens.some(
                  dishAllergen =>
                    dishAllergen === allergen ||
                    dishAllergen.toLowerCase() === allergen.toLowerCase() ||
                    dishAllergen.toLowerCase().replace(/\s+/g, '') ===
                      allergen.toLowerCase().replace(/\s+/g, ''),
                )

                // Check for "Traces" allergens
                const hasTraces = dishAllergenTraces.some(
                  dishAllergen =>
                    dishAllergen === allergen ||
                    dishAllergen.toLowerCase() === allergen.toLowerCase() ||
                    dishAllergen.toLowerCase().replace(/\s+/g, '') ===
                      allergen.toLowerCase().replace(/\s+/g, ''),
                )

                // "Yes" overrides "Traces"
                if (hasAllergen) {
                  return (
                    <TableCell key={allergen}>
                      <CheckOutlined style={{ color: '#52c41a' }} />
                    </TableCell>
                  )
                } else if (hasTraces) {
                  return (
                    <TableCell key={allergen}>
                      <span style={{ color: '#fa8c16', fontWeight: 'bold' }}>
                        May Contain
                      </span>
                    </TableCell>
                  )
                } else {
                  return <TableCell key={allergen}></TableCell>
                }
              })}
            </tr>,
          )
        })
      }
    })

    return (
      <AllergenTable>
        <thead>
          <tr>
            <TableHeader>Dishes</TableHeader>
            {defaultAllergens.map(allergen => (
              <TableHeader key={allergen}>{allergen}</TableHeader>
            ))}
          </tr>
        </thead>
        <tbody>{tableRows}</tbody>
      </AllergenTable>
    )
  }

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <Spin size='large' />
        </LoadingContainer>
      </PageContainer>
    )
  }

  if (error) {
    return (
      <PageContainer>
        <Content>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <h2>Error Loading Allergen Information</h2>
            <p>{error}</p>
          </div>
        </Content>
      </PageContainer>
    )
  }

  if (!menuData) {
    return (
      <PageContainer>
        <Content>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <h2>No Menu Data Available</h2>
          </div>
        </Content>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <Header>
        {menuData.venueLogo && (
          <VenueLogo src={menuData.venueLogo} alt='Venue Logo' />
        )}
        <VenueName>{menuData.venueName || 'Bonnie Burrito'}</VenueName>
        <MenuName>
          {menuData.menuName || 'Menu'} - Allergen Information
        </MenuName>
      </Header>

      <SearchContainer>
        <SearchInput
          type='text'
          placeholder='Search dishes...'
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
      </SearchContainer>

      <Content>{renderAllergenTable()}</Content>

      <Footer>
        <div>Powered by Orders Made Simple</div>
        <div>{new Date().toLocaleDateString()}</div>
      </Footer>

      <RotateDeviceOverlay>
        <RotateIcon>📱</RotateIcon>
        <RotateMessage>Please rotate your device</RotateMessage>
        <RotateSubMessage>
          This allergen table is best viewed in landscape mode
        </RotateSubMessage>
      </RotateDeviceOverlay>
    </PageContainer>
  )
}

export default CustomerAllergenPage
